import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import {Link} from '@reach/router';
import React, {useEffect, useState} from 'react';
import PageTitle from '../../Components/PageTitle/PageTitle';
import Row from '../../Components/Layout/Row';
import {apiRequestHelper} from '../../Helpers/ApiRequestHelper';
import {timestampToDateTime} from '../../Localization/DateFormatter';

interface ConflictResolutionLogResolution {
  conflict_type: number,
  decision: number,
  options?: any[],
  chosen_option_id?: number | null
}

interface ConflictResolutionLog {
  id: number,
  user_id: number,
  resolution: ConflictResolutionLogResolution,
  after?: {
    student?: { id: number },
    teacher1?: { id: number },
    teacher2?: { id: number },
    result?: { id: number }
  },
  changed?: any,
  created_at: string
}

interface ConflictResolutionHistoryProps {
  uploadId: string,
  reloadKey: number
}

const conflictTypeLabels: Record<number, string> = {
  1: 'Ученик: смена ОУ',
  2: 'Учитель 1: смена ОУ',
  3: 'Учитель 2: смена ОУ',
  4: 'Ученик: возможный дубликат',
  5: 'Учитель 1: возможный дубликат',
  6: 'Учитель 2: возможный дубликат',
} as const;

const decisionLabels: Record<number, string> = {
  1: 'Создание записи',
  2: 'Использование существующего',
} as const;

const entityLabels: Record<string, string> = {
  student: 'Ученик',
  teacher1: 'Учитель 1',
  teacher2: 'Учитель 2',
  result: 'Результат',
} as const;

/** Lookup: conflict type to entity base path */
const CONFLICT_TYPE_PATHS: Record<number, string> = {
  1: '/student', 4: '/student',
  2: '/teacher', 3: '/teacher', 5: '/teacher', 6: '/teacher',
};

/** Lookup: conflict type to entity key in after object */
const CONFLICT_TYPE_ENTITY_KEY: Record<number, 'student' | 'teacher1' | 'teacher2'> = {
  1: 'student', 4: 'student',
  2: 'teacher1', 5: 'teacher1',
  3: 'teacher2', 6: 'teacher2',
};

/** Lookup: field key to entity path for link generation */
const FIELD_TO_PATH: Record<string, string> = {
  district_id: 'district',
  school_id: 'school',
};

/**
 * Reusable link component for entity references.
 */
const EntityLink = ({path, id}: {path: string; id: number}) => (
  <Link to={`/${path}/${id}`}>{id}</Link>
);

/**
 * Wrapper component for loading/error/empty states.
 */
const StateMessage = ({
  children,
  color = 'textSecondary',
}: {
  children: React.ReactNode;
  color?: 'textSecondary' | 'error';
}) => (
  <>
    <PageTitle title="История разрешения конфликтов"/>
    <Row>
      <div>
        <Typography variant="body2" color={color}>
          {children}
        </Typography>
      </div>
    </Row>
  </>
);

const getConflictTypeLabel = (conflictType?: number) =>
  conflictType ?
    (conflictTypeLabels[conflictType] ?? `Тип конфликта #${conflictType}`) :
    'Конфликт';

const getDecisionLabel = (decision?: number) =>
  decision ?
    (decisionLabels[decision] ?? `Решение #${decision}`) :
    'Решение не указано';

type FieldTitleValue = string | null | ((entityKey: string) => string);

/** Lookup: field key to localized title */
const FIELD_TITLES: Record<string, FieldTitleValue> = {
  updated_at: null,
  district_id: 'Район',
  school_id: (entityKey) =>
    entityKey === 'student' ?
      'ОУ ученика' :
      'ОУ наставника',
};

/**
 * Returns localized field title for changed field or null if it should be skipped.
 */
const getFieldTitle = (entityKey: string, fieldKey: string): string | null => {
  const title = FIELD_TITLES[fieldKey];
  if (title === null) return null;
  if (typeof title === 'function') return title(entityKey);
  return title ?? fieldKey;
};

/**
 * Formats any value for display, handling null/undefined and objects.
 */
const formatValue = (value: any): string =>
  value == null ?
    '—' :
    typeof value === 'object' ?
      JSON.stringify(value) :
      String(value);

/**
 * Returns base entity URI prefix based on conflict type.
 */
const getEntityBasePathForConflictType = (conflictType?: number): string | null =>
  CONFLICT_TYPE_PATHS[conflictType!] ?? null;

/**
 * Extracts numeric id from option payload which can be a raw id or object with id field.
 */
const getOptionId = (option: any): number | null => {
  if (typeof option === 'number') return option;
  return typeof option?.id === 'number' ? option.id : null;
};

/**
 * Returns a link to the selected entity (student/teacher) or a dash if data is missing.
 * For creation (decision=1), gets ID from log.after; for update (decision=2), uses chosen_option_id.
 */
const getChosenEntityLink = (log: ConflictResolutionLog) => {
  const {resolution, after} = log;
  const basePath = getEntityBasePathForConflictType(resolution?.conflict_type);

  if (!basePath) return '—';

  // For creation, get ID from the after object
  if (resolution?.decision === 1) {
    const entityKey = CONFLICT_TYPE_ENTITY_KEY[resolution.conflict_type];
    const entityId = after?.[entityKey]?.id;
    if (!entityId) return '—';
    return <EntityLink path={basePath.slice(1)} id={entityId} />;
  }

  // For update, use chosen_option_id
  const chosenId = resolution?.chosen_option_id;
  if (!chosenId) return '—';
  return <EntityLink path={basePath.slice(1)} id={chosenId} />;
};

/**
 * Returns comma-separated links to candidate entities for the conflict.
 */
const getCandidatesLinks = (log: ConflictResolutionLog) => {
  const {resolution} = log;
  const basePath = getEntityBasePathForConflictType(resolution?.conflict_type);
  const options = resolution?.options ?? [];

  if (!basePath || !options.length) return '—';

  const ids = options
    .map(getOptionId)
    .filter((id): id is number => typeof id === 'number');

  if (!ids.length) return '—';

  return (
    <>
      {ids.map((id, index) => (
        <React.Fragment key={id}>
          {index > 0 && ', '}
          <EntityLink path={basePath.slice(1)} id={id} />
        </React.Fragment>
      ))}
    </>
  );
};

/**
 * Returns a link to the resulting entity or a dash if result id is not present.
 */
const getResultLink = (log: ConflictResolutionLog) => {
  const resultId = log?.changed?.result?.id?.after;
  if (!resultId) return '—';
  return <EntityLink path="result" id={resultId} />;
};

/**
 * Renders a single changed value, turning district and school ids into links.
 */
const renderChangeValue = (fieldKey: string, value: any) => {
  const path = FIELD_TO_PATH[fieldKey];
  if (path) {
    const id = getOptionId(value);
    if (typeof id === 'number') return <EntityLink path={path} id={id} />;
  }
  return formatValue(value);
};

/**
 * Renders list of changed fields grouped by entity with localized field titles.
 * Returns null for creation decisions (decision=1) since all fields are new.
 */
const renderChanges = (changed: any, decision?: number) => {
  if (decision === 1) return null;
  if (!changed || typeof changed !== 'object') return null;

  const entities = Object.entries(changed).filter(([key]) => key !== 'result');
  if (!entities.length) return null;

  return (
    <div style={{marginTop: 4}}>
      {entities.map(([entityKey, fields]) => {
        if (!fields || typeof fields !== 'object') return null;

        const fieldEntries = Object.entries(fields as Record<string, any>);
        const hasVisibleFields = fieldEntries.some(([fieldKey, fieldChange]) => {
          const fieldTitle = getFieldTitle(entityKey, fieldKey);
          return fieldChange && fieldTitle;
        });
        if (!hasVisibleFields) return null;

        return (
          <div key={entityKey} style={{marginTop: 4}}>
            {fieldEntries.map(([fieldKey, fieldChange]) => {
              if (!fieldChange) return null;

              const fieldTitle = getFieldTitle(entityKey, fieldKey);
              if (!fieldTitle) return null;

              return (
                <Typography
                  key={fieldKey}
                  variant="body2"
                  color="textSecondary"
                >
                  {fieldTitle}
                  {': '}
                  {renderChangeValue(fieldKey, fieldChange.before)}
                  {' → '}
                  {renderChangeValue(fieldKey, fieldChange.after)}
                </Typography>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

const useResultUploadLogs = (id?: string, reloadKey?: number) => {
  const [logs, setLogs] = useState<ConflictResolutionLog[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    if (!id) {
      setLogs([]);
      setIsLoading(false);
      setIsError(false);
      return;
    }

    const responseHandler = (response: any) => {
      const responseData = response?.data?.data || [];
      setLogs(responseData);
      setIsLoading(false);
    };

    const errorHandler = () => {
      setIsError(true);
      setIsLoading(false);
    };

    setIsLoading(true);
    setIsError(false);

    apiRequestHelper({
      action: 'RESULT_UPLOAD_LOGS',
      id,
      responseHandler,
      errorHandler,
    });
  }, [id, reloadKey]);

  return {logs, isLoading, isError};
};

const ConflictResolutionHistory = ({
  uploadId,
  reloadKey,
}: ConflictResolutionHistoryProps) => {
  const {logs, isLoading, isError} = useResultUploadLogs(uploadId, reloadKey);

  if (isLoading) {
    return <StateMessage>Загрузка истории...</StateMessage>;
  }

  if (isError) {
    return (
      <StateMessage color="error">
        Не удалось загрузить историю разрешения конфликтов.
      </StateMessage>
    );
  }

  if (logs.length === 0) {
    return (
      <StateMessage>
        Для этой загрузки пока нет записей об истории разрешения конфликтов.
      </StateMessage>
    );
  }

  return (
    <>
      <PageTitle title="История разрешения конфликтов"/>

      <Row>
        <div>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Тип конфликта</TableCell>
                  <TableCell>Выбранный способ разрешения</TableCell>
                  <TableCell>Созданный/выбранный ученик/учитель</TableCell>
                  <TableCell>Кандидаты на разрешение конфликта</TableCell>
                  <TableCell>Результат ученика</TableCell>
                  <TableCell>Пользователь</TableCell>
                  <TableCell>Внесенные изменения</TableCell>
                  <TableCell>Время</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map(log => {
                  const {id, resolution, user_id, changed, created_at} = log;
                  const conflictLabel = getConflictTypeLabel(resolution?.conflict_type);
                  const decisionLabel = getDecisionLabel(resolution?.decision);
                  const timestamp = timestampToDateTime(created_at);

                  return (
                    <TableRow key={id}>
                      <TableCell>
                        <Typography variant="body2">{conflictLabel}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{decisionLabel}</Typography>
                      </TableCell>
                      <TableCell>{getChosenEntityLink(log)}</TableCell>
                      <TableCell>{getCandidatesLinks(log)}</TableCell>
                      <TableCell>{getResultLink(log)}</TableCell>
                      <TableCell>
                        {user_id ? <EntityLink path="user" id={user_id} /> : '—'}
                      </TableCell>
                      <TableCell>{renderChanges(changed, resolution?.decision)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{timestamp}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Row>
    </>
  );
};

export default ConflictResolutionHistory;
